/**
 * 编辑用户信息弹窗管理器
 * 
 * 功能：
 * - 打开/关闭弹窗
 * - 表单验证（必填：username）
 * - POST /a/ores/profile/update 提交昵称、生日
 * - 成功后关闭弹窗并刷新页面
 * 
 * 依赖：
 * - RoDialog.js
 * - RoInput.js
 * - RoDatePicker.js
 */

(function() {
  'use strict';

  // 避免重复初始化
  if (window.EditUserInfoManager) {
    return;
  }

  /**
   * 编辑用户信息管理器
   */
  class EditUserInfoManager {
    constructor() {
      this.dialog = null;
      this.form = null;
      this.usernameInput = null;
      this.birthdayPicker = null;
      this.subtextElement = null;
      this.saveButton = null;
      this.cancelButton = null;
      this.errorContainer = null;
      this.isLoading = false;

      this._init();
    }

    /**
     * 初始化
     */
    _init() {
      // 等待 DOM 和自定义元素加载完成
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._setup());
      } else {
        this._setup();
      }
    }

  /**
   * 设置元素引用和事件监听
   */
  async _setup() {
    // 等待自定义元素注册完成
    try {
      await Promise.all([
        customElements.whenDefined('ro-dialog'),
        customElements.whenDefined('ro-input'),
        customElements.whenDefined('ro-date-picker')
      ]);
    } catch (error) {
      console.error('[EditUserInfo] Custom elements not defined:', error);
    }

    // 获取元素引用
    this.dialog = document.querySelector('#edit-user-info-dialog');
    this.form = document.querySelector('#edit-user-info-form');
    this.usernameInput = document.querySelector('#edit-username');
    this.birthdayPicker = document.querySelector('#edit-birthday');
    this.subtextElement = document.querySelector('.edit-user-info__subtext');
    this.saveButton = document.querySelector('#edit-user-info-save');
    this.cancelButton = document.querySelector('#edit-user-info-cancel');
    this.errorContainer = document.querySelector('#edit-user-info-error');

    // 用户名最多 20 个字符
    this.usernameInput?.setAttribute('max-length', '20');

    if (!this.dialog || !this.form) {
      console.warn('[EditUserInfo] Required elements not found');
      return;
    }

    // 检查 dialog 是否有 open 方法
    if (typeof this.dialog.open !== 'function') {
      console.error('[EditUserInfo] Dialog element does not have open method. Make sure RoDialog.js is loaded.');
      return;
    }

    this._bindEvents();
  }

    /**
     * 绑定事件
     */
    _bindEvents() {
      // 表单提交
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._handleSubmit();
      });

      // 取消按钮
      this.cancelButton?.addEventListener('click', () => {
        this.close();
      });

      // 弹窗关闭时清理状态
      this.dialog.addEventListener('onclose', () => {
        this._resetForm();
      });

      // 输入时清除错误
      this.usernameInput?.addEventListener('input', () => {
        this._clearError();
        this.usernameInput.clearError();
      });
    }

  /**
   * 获取用户资料（GET /a/ores/profile）
   * @returns {Promise<{ nick: string, birthday: string, profilePoints: number }>}
   */
  async _fetchProfile() {
    const { data } = await axios({
      method: 'GET',
      url: '/a/ores/profile'
    });
    const profile = data && data.data ? data.data : {};
    const rawPoints = profile.profilePoints;
    const profilePoints =
      typeof rawPoints === 'number'
        ? rawPoints
        : rawPoints != null && !Number.isNaN(Number(rawPoints))
        ? Number(rawPoints)
        : 0;

    return {
      nick: profile.nick != null ? String(profile.nick) : '',
      birthday: profile.birthday != null ? String(profile.birthday) : '',
      profilePoints: profilePoints
    };
  }

  /**
   * 将接口返回的 nick、birthday 填充到表单
   */
  _fillFormFromProfile(nick, birthday) {
    if (this.usernameInput) {
      this.usernameInput.value = nick || '';
    }
    if (this.birthdayPicker) {
      this.birthdayPicker.value = birthday || '';
    }
  }

  /**
   * 使用 profilePoints 更新副标题中的积分占位符
   * 将文案中的 ${...} 替换为实际积分数字
   * 如果接口没有返回有效的 profilePoints，则视为 0 并隐藏文案
   * @param {number | null | undefined} profilePoints
   */
  _updateSubtextWithPoints(profilePoints) {
    if (!this.subtextElement) return;

    const safePoints =
      typeof profilePoints === 'number' && !Number.isNaN(profilePoints)
        ? profilePoints
        : 0;

    // profilePoints 不存在或为 0 时，不展示副标题
    if (safePoints <= 0) {
      this.subtextElement.style.display = 'none';
      return;
    }

    // 有有效积分时展示副标题并替换占位符
    this.subtextElement.style.display = '';

    const text = this.subtextElement.textContent || '';
    if (!text.includes('${')) return;

    const pointsText = String(safePoints);
    this.subtextElement.textContent = text.replace(/\$\{[^}]*\}/, pointsText);
  }

  /**
   * 打开弹窗，优先使用侧边栏已缓存的 profile 数据，避免重复请求接口
   */
  open() {
    if (!this.dialog) {
      console.warn('[EditUserInfo] Dialog not initialized');
      return;
    }

    if (typeof this.dialog.open !== 'function') {
      console.error('[EditUserInfo] Dialog.open is not a function. RoDialog may not be loaded.');
      return;
    }

    this._clearError();

    try {
      this.dialog.open();

      if (window._oresProfile) {
        // 使用页面加载时已缓存的数据，直接填充
        const { nick, birthday, profilePoints } = window._oresProfile;
        this._fillFormFromProfile(nick, birthday);
        this._updateSubtextWithPoints(profilePoints);
      } else {
        // 缓存不存在时（如直接访问弹窗页面）兜底请求接口
        this._fillFormFromProfile('', '');
        this._fetchProfile()
          .then(({ nick, birthday, profilePoints }) => {
            this._fillFormFromProfile(nick, birthday);
            this._updateSubtextWithPoints(profilePoints);
          })
          .catch((err) => {
            console.warn('[EditUserInfo] Load profile failed:', err);
          });
      }
    } catch (error) {
      console.error('[EditUserInfo] Error opening dialog:', error);
    }
  }

    /**
     * 关闭弹窗
     */
    close() {
      if (!this.dialog) return;
      this.dialog.close();
    }

    /**
     * 处理表单提交
     */
    async _handleSubmit() {
      // 防止重复提交
      if (this.isLoading) return;

      // 校验表单
      const isValid = this._validateForm();
      if (!isValid) return;

      // 获取表单数据
      const formData = this._getFormData();

      // 显示加载状态
      this._setLoading(true);
      this._clearError();

      try {
        // 模拟 API 请求
        await this._submitData(formData);

        // 成功：关闭弹窗并刷新页面
        this.close();
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } catch (error) {
        // 失败：显示错误信息
        this._showError(error.message || 'An error occurred. Please try again.');
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * 验证表单（完全依赖各组件自身的校验样式和文案）
     * @returns {boolean}
     */
    _validateForm() {
      const results = [];

      if (this.usernameInput) {
        results.push(this.usernameInput.validate().valid);
      }

      if (this.birthdayPicker) {
        results.push(this.birthdayPicker.validate().valid);
      }

      return results.every(Boolean);
    }

    /**
     * 获取表单数据
     * @returns {Object}
     */
    _getFormData() {
      return {
        username: this.usernameInput?.value?.trim() || '',
        birthday: this.birthdayPicker?.value || null
      };
    }

    /**
     * 提交数据到 POST /a/ores/profile/update
     * @param {Object} data - { nick: 用户昵称, birthday: 用户生日 }
     * @returns {Promise}
     */
    _submitData(data) {
      return axios({
        method: 'POST',
        url: '/a/ores/profile/update',
        data: {
          nick: data.username,
          birthday: data.birthday
        }
      })
        .then((response) => {
          const res = response.data;
          if (res && res.code !== undefined && res.code !== 0) {
            throw new Error(res.message || '更新失败，请重试');
          }
          return res;
        })
        .catch((error) => {
          const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            '更新失败，请重试';
          throw new Error(message);
        });
    }

    /**
     * 设置加载状态
     * @param {boolean} loading
     */
    _setLoading(loading) {
      this.isLoading = loading;

      if (!this.saveButton) return;

      const btnText = this.saveButton.querySelector('.edit-user-info__btn-text');
      const btnLoading = this.saveButton.querySelector('.edit-user-info__btn-loading');

      if (loading) {
        this.saveButton.disabled = true;
        btnText.style.display = 'none';
        btnLoading.style.display = 'inline-flex';
      } else {
        this.saveButton.disabled = false;
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
      }
    }

    /**
     * 显示错误信息
     * @param {string} message
     */
    _showError(message) {
      if (!this.errorContainer) return;
      this.errorContainer.textContent = message;
      this.errorContainer.style.display = 'block';
    }

    /**
     * 清除错误信息
     */
    _clearError() {
      if (!this.errorContainer) return;
      this.errorContainer.textContent = '';
      this.errorContainer.style.display = 'none';
    }

    /**
     * 重置表单
     */
    _resetForm() {
      this._clearError();
      this._setLoading(false);
      
      // 重置输入框和日期选择器错误状态
      this.usernameInput?.clearError();
      this.birthdayPicker?.clearError();
    }
  }

  // 创建全局实例
  let managerInstance = null;
  
  // 初始化管理器（延迟创建，确保 DOM 准备就绪）
  function initializeManager() {
    if (!managerInstance) {
      managerInstance = new EditUserInfoManager();
    }
    return managerInstance;
  }

  // 暴露打开方法到全局（方便在 Liquid 中调用）
  window.openEditUserInfoDialog = function() {
    const manager = initializeManager();
    
    // 如果管理器还没准备好，等待一下
    if (!manager.dialog) {
      console.warn('[EditUserInfo] Manager not ready, retrying...');
      setTimeout(() => {
        window.openEditUserInfoDialog();
      }, 100);
      return;
    }
    
    manager.open();
  };

  // 为了向后兼容，也暴露管理器实例
  Object.defineProperty(window, 'EditUserInfoManager', {
    get: function() {
      return initializeManager();
    }
  });

})();
